/**
 * useFreemium.ts — Amargi (Supabase)
 *
 * Gère la limite freemium : 2 évaluations par tranche de 12h.
 * Le rate limiting est stocké côté serveur (table users, colonnes
 * rate_limit_count et rate_limit_window_start) pour résister au
 * vidage du localStorage.
 *
 * Bypass automatique si users.plan est 'trial_7j', 'trial_8j', 'beta' ou 'pro'.
 * markEvaluationDone() est appelé dès que le quiz passe en phase 'analysis'.
 */

import { useState, useEffect, useCallback } from 'react';
import { getSupabaseLazy } from '../lib/supabase';
import { getOrCreateUserIdAsync } from '../lib/userSession';

export type UserPlan = 'free' | 'trial_7j' | 'trial_8j' | 'beta' | 'pro';

const DAILY_LIMIT = 2;
const WINDOW_MS   = 12 * 60 * 60 * 1000; // 12h en ms

const PAID_PLANS: UserPlan[] = ['trial_7j', 'trial_8j', 'beta', 'pro'];

export interface FreemiumState {
  canEvaluate: boolean;
  countToday: number;
  dailyLimit: number;
  nextAvailableAt: Date;
  plan: UserPlan;
  isPaid: boolean;
  markEvaluationDone: () => void;
  resetLimit: () => void;
  loading: boolean;
}

export function useFreemium(): FreemiumState {
  const [count, setCount]             = useState(0);
  const [windowStart, setWindowStart] = useState(Date.now());
  const [loading, setLoading]         = useState(true);
  const [plan, setPlan]               = useState<UserPlan>('free');
  const [userId, setUserId]           = useState<string | null>(null);

  // Charger l'état depuis le serveur au montage
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uid = await getOrCreateUserIdAsync();
        if (cancelled) return;
        setUserId(uid);

        const supabase = await getSupabaseLazy();
        const { data } = await supabase
          .from('users')
          .select('plan, rate_limit_count, rate_limit_window_start')
          .eq('id', uid)
          .maybeSingle();

        if (cancelled) return;

        if (data) {
          const userPlan = (data.plan as UserPlan) ?? 'free';
          setPlan(userPlan);

          const winStart = data.rate_limit_window_start
            ? new Date(data.rate_limit_window_start).getTime()
            : Date.now();
          const now = Date.now();

          if (now - winStart >= WINDOW_MS) {
            // Fenêtre expirée — reset silencieux
            setCount(0);
            setWindowStart(now);
          } else {
            setCount((data.rate_limit_count as number) ?? 0);
            setWindowStart(winStart);
          }
        }
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[useFreemium] init error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Écouter le plan en temps réel (Supabase Realtime)
  useEffect(() => {
    if (!userId) return;
    let channel: ReturnType<Awaited<ReturnType<typeof getSupabaseLazy>>['channel']> | null = null;

    (async () => {
      const supabase = await getSupabaseLazy();
      channel = supabase
        .channel(`users:${userId}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'users', filter: `id=eq.${userId}` },
          (payload) => {
            const newPlan = (payload.new as { plan?: UserPlan }).plan ?? 'free';
            setPlan(newPlan);
          }
        )
        .subscribe();
    })();

    return () => {
      if (channel) channel.unsubscribe();
    };
  }, [userId]);

  const isPaid = PAID_PLANS.includes(plan);
  const canEvaluate = isPaid || count < DAILY_LIMIT;

  const markEvaluationDone = useCallback(() => {
    if (isPaid || !userId) return;

    const now = Date.now();
    const isNewWindow = now - windowStart >= WINDOW_MS;
    const newWindowStart = isNewWindow ? now : windowStart;
    const newCount = isNewWindow ? 1 : count + 1;

    setCount(newCount);
    if (isNewWindow) setWindowStart(newWindowStart);

    // Persistance serveur asynchrone
    getSupabaseLazy().then(supabase =>
      supabase.from('users').update({
        rate_limit_count:        newCount,
        rate_limit_window_start: new Date(newWindowStart).toISOString(),
      }).eq('id', userId)
    ).catch(e => {
      if (import.meta.env.DEV) console.warn('[useFreemium] markEvaluationDone persist error:', e);
    });
  }, [isPaid, userId, count, windowStart]);

  const resetLimit = useCallback(() => {
    const now = Date.now();
    setCount(0);
    setWindowStart(now);

    if (userId) {
      getSupabaseLazy().then(supabase =>
        supabase.from('users').update({
          rate_limit_count:        0,
          rate_limit_window_start: new Date(now).toISOString(),
        }).eq('id', userId)
      ).catch(e => {
        if (import.meta.env.DEV) console.warn('[useFreemium] resetLimit persist error:', e);
      });
    }
  }, [userId]);

  const nextAvailableAt = new Date(windowStart + WINDOW_MS);

  return {
    canEvaluate,
    countToday: count,
    dailyLimit: DAILY_LIMIT,
    nextAvailableAt,
    plan,
    isPaid,
    markEvaluationDone,
    resetLimit,
    loading,
  };
}
